<template>
  <div class="faqsets-form">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
      :loading="loading"
    >
      <template #actions v-if="!isOnlyView">
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission"
            @click="actionFaqset"
          >
            <span class="px-2">{{ faqsetId ? 'Save' : 'Create'}}</span>
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="d-flex align-items-center justify-content-between">
        <FormTitle title="General info" />
        <a-button
          v-if="isOnlyView"
          size="large"
          :disabled="!editPermission"
          @click="goToEdit"
        >
          <span class="px-2">Edit</span>
        </a-button>
      </div>
      <a-form-model :model="form" ref="form">
        <a-row class="d-flex">
          <Readonly
            :show="isOnlyView && !!form.name"
            title="Name"
            :value="form.name"
            style="max-width: 270px; align-self: flex-start;"
            class="mb-3"
          >
            <a-form-model-item
              label="Name"
              prop="name"
              :colon="false"
              :rules="[{ required: true, message: 'Name is required', trigger: ['blur', 'change'] }]"
              class="mr-5"
              style="max-width: 270px"
            >
              <a-input
                v-model="form.name"
                style="width: 270px;"
                placeholder="Name"
                size="large"
                allow-clear
                :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                :disabled="loading || isOnlyView"
              />
            </a-form-model-item>
          </Readonly>
        </a-row>
        <a-row class="d-flex">
          <Readonly
            :show="isOnlyView && !!currentFaqsetRoleName || isEditMode"
            title="Role"
            :value="currentFaqsetRoleName"
            style="max-width: 270px; align-self: flex-start;"
            :class="{'mb-4': isEditMode}"
          >
            <a-form-model-item
              v-if="availableRoles.length"
              prop="role_id"
              :colon="false"
              :rules="[{ required: true, message: 'Role is required', trigger: ['blur', 'change'] }]"
              class="label-with-tooltip"
            >
              <template #label>
                <div>
                  <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Role</span>
                  <a-tooltip
                    v-if="!isEditMode"
                    placement="top"
                    arrow-point-at-center
                    :overlayStyle="{ 'white-space': 'normal', 'min-width': '240px' }"
                    :get-popup-container="getPopupContainer"
                  >
                    <template slot="title">
                      <span>Can be changed only in create mode</span>
                    </template>
                    <a-icon class="d-flex" type="info-circle" />
                  </a-tooltip>
                </div>
              </template>
              <a-radio-group
                v-model="form.role_id"
                name="radioGroup"
                :disabled="isOnlyView"
                class="d-flex"
                style="gap: 20px;"
              >
                <a-radio
                  v-for="role in availableRoles"
                  :key="role.id"
                  :value="role.id"
                  :disabled="getAvailableRolesLoading"
                >
                  {{ role.name }}
                </a-radio>
              </a-radio-group>
            </a-form-model-item>
          </Readonly>
        </a-row>
        <FormTitle title="FAQs" tooltipText="These FAQs will show on the referral screen when the info icon is tapped" />
        <a-tabs
          v-model="activeLanguage"
          :animated="{ inkBar: true, tabPane: false }"
          @change="changeActiveLanguageTab"
        >
          <a-tab-pane
            v-for="langTab in form.languages"
            :key="langTab.language.id"
            force-render
          >
            <template #tab>
              <DefaultLabelTab
                :name="langTab.language.name"
                :error_validate="langTab.error_validate"
                :is_default="langTab.language.is_default"
              />
            </template>
            <div class="d-flex justify-content-between align-items-center">
              <a-button
                v-if="!isOnlyView"
                type="primary"
                size="large"
                :disabled="!createPermission"
                @click="toggleShowFaqModal(true)"
              >
                <a-icon type="plus" /> Add
              </a-button>
              <span
                v-if="!isOnlyView && Object.keys(form.languages).length > 1 && langTab.items.length"
                :class="disableCopy ? 'text-default cursor-default' : 'text-primary cursor-pointer underline'"
                @click="copyToOtherLanguages(langTab.language.name)"
              >
                Copy to other languages
              </span>
            </div>
            <div class="d-flex flex-wrap" style="max-width: 665px; gap: 15px;">
              <div v-if="!langTab.items.length" class="text-secondary mt-4">
                <span v-if="langTab.error_validate" class="text-danger">The FAQs are mandatory</span>
                <span v-else>No FAQs have been added</span>
              </div>
              <div v-else :class="{'mt-4': !isOnlyView}">
                <draggable
                  v-model="langTab.items"
                  v-bind="dragOptions"
                  handle=".handle"
                  @start="drag = true"
                  @end="drag = false"
                >
                  <transition-group tag="div" class="faqsets-form__faqs">
                    <div
                      v-for="item in langTab.items"
                      :key="`${item.uniqueId}-${langTab.language.id}`"
                      class="faqsets-form__faq"
                    >
                      <div class="d-flex align-self-start align-items-center justify-content-center ">
                        <a-icon
                          v-if="!isOnlyView"
                          type="menu"
                          class="drag-icon handle faqsets-form__handle__icon faqsets-form__handle__icon__hide"
                          :class="{'faqsets-form__handle__icon__show mr-2' : langTab.items.length > 1}"
                        />
                      </div>
                      <a-collapse accordion class="faqsets-form__collapse">
                        <a-collapse-panel
                          :key="item.uniqueId"
                          :header="item.name"
                        >
                          <p>{{ item.description }}</p>
                        </a-collapse-panel>
                      </a-collapse>
                      <div class="d-flex align-self-start align-items-center justify-content-center">
                        <a-button
                          v-if="!isOnlyView"
                          type="ghost"
                          style="width: 46px; height: 46px; padding: 0;"
                          class="ml-2"
                          @click="toggleShowFaqModal(true, item)"
                        >
                          <a-icon type="edit" class="text-primary font-size-18" />
                        </a-button>
                      </div>
                    </div>
                  </transition-group>
                </draggable>
              </div>
            </div>
          </a-tab-pane>
          <span
            v-if="!isOnlyView"
            slot="tabBarExtraContent"
            class="text-primary cursor-pointer underline"
            @click="toggleShowEditLanguagesModal(true)"
          >
            Edit languages
          </span>
        </a-tabs>
      </a-form-model>
    </MainContainer>
    <div v-if="!isOnlyView && isEditMode && deletePermission" class="mt-3">
      <div
        class="d-flex align-items-center text-danger cursor-pointer hover-underline"
        style="width: fit-content;"
        @click="toggleShowDeleteModal(true)"
      >
        <a-icon type="delete" class="mr-2" />
        <span>Delete FAQset</span>
      </div>
    </div>
    <EditLanguagesModal
      v-if="showEditLanguagesModal"
      :show="showEditLanguagesModal"
      :allLanguages="availableLanguages"
      :languagesUsed="languagesUsed"
      @update="updateLanguages"
      @close="toggleShowEditLanguagesModal"
    />
    <AddFaqModal
      v-if="showFaqModal"
      :show="showFaqModal"
      :selectedFaq="editableFaq"
      @create="createFaq"
      @update="updateFaq"
      @delete="deleteFaq"
      @close="toggleShowFaqModal"
    />
    <DefaultModal
      :show="showDeleteModal"
      title="Delete Faqset"
      text="Are you sure you want to delete this Faqset?"
      description="All associated data will be lost"
      :actionButton="{
        text: 'Delete',
        type: 'danger',
      }"
      :loading="loading"
      @close="toggleShowDeleteModal"
      @action="removeFaqset"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import FormTitle from '@/components/custom/form/FormTitle.vue'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import DefaultModal from '@/components/custom/modals/defaultModal'
import Readonly from '@/components/custom/readonlyMode/readonly.vue'
import AddFaqModal from '@/views/apps/referral-program/components/AddFaqModal.vue'
import EditLanguagesModal from '@/views/apps/referral-program/components/EditLanguagesModal.vue'
import draggable from 'vuedraggable'
import { getReferralRoles } from '@/views/apps/referral-program/composables/useGetReferralRoles.js'
import { getReferralLanguages } from '@/views/apps/referral-program/composables/useGetReferralLanguages.js'
import { getRandomId } from '@/composables/useGetRandomId.js'
import { defaultLanguageName } from '@/views/apps/referral-program/constants/defaultLanguageName.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    FormTitle,
    DefaultLabelTab,
    draggable,
    Readonly,
    EditLanguagesModal,
    AddFaqModal,
    DefaultModal,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    faqsetId: null,
    currentFaqset: null,
    editableFaq: null,
    availableRoles: [],
    getAvailableRolesLoading: false,
    availableLanguages: [],
    getAvailableLanguagesLoading: false,
    activeLanguage: 1,
    disableCopy: false,
    form: {
      name: '',
      role_id: 1,
      languages: {},
    },
    loading: false,
    showEditLanguagesModal: false,
    showFaqModal: false,
    showDeleteModal: false,
    drag: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isOnlyView() {
      return this.$route.name === 'view-faqsets'
    },
    isEditMode() {
      return !!this.faqsetId
    },
    breadcrumbs() {
      if (this.currentFaqset) {
        if (this.isOnlyView) {
          return [
            { name: 'FAQsets', link: '/referral/faqsets' },
            { name: 'View faqsets', link: undefined },
          ]
        }
        return [
          { name: 'FAQsets', link: '/referral/faqsets' },
          { name: this.currentFaqset?.name, link: `/referral/faqsets/${this.currentFaqset.id}/view` },
          { name: 'Edit faqsets', link: undefined },
        ]
      }
      return [
        { name: 'FAQsets', link: '/referral/faqsets' },
        { name: 'Create faqsets', link: undefined },
      ]
    },
    dragOptions() {
      return {
        animation: 300,
        disabled: false,
        ghostClass: 'ghost',
      }
    },
    activeLanguageName() {
      const activeLanguage = this.availableLanguages.find(lang => lang.id === this.activeLanguage)
      if (activeLanguage) {
        return activeLanguage.name
      }
      return ''
    },
    languagesUsed() {
      return Object.values(this.form.languages).map(lang => ({
        name: lang.language.name,
        value: lang.language.id,
        is_default: lang.language.is_default,
      }))
    },
    currentFaqsIds() {
      if (this.form.languages[this.activeLanguageName].items?.length) {
        return this.form.languages[this.activeLanguageName].items.map(rule => rule.id)
      }
      return []
    },
    currentFaqsetRoleName() {
      return this.currentFaqset?.role?.name
    },
  },
  watch: {
    drag() {
      this.disableCopy = false
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    goToEdit() {
      this.$router.push(`/referral/faqsets/${this.faqsetId}`)
    },
    setAvailableFaqsLanguages(languages = []) {
      languages.forEach(lang => {
        this.setAvailableLanguage(lang)
      })
    },
    setAvailableLanguage(language, items = []) {
      this.$set(
        this.form.languages,
        language.name,
        {
          language: { id: language.id, name: language.name, is_default: language.is_default },
          items: items,
          error_validate: false,
        },
      )
    },
    changeActiveLanguageTab() {
      this.clearError()
    },
    clearError() {
      this.form.languages[this.activeLanguageName].error_validate = false
    },
    async copyToOtherLanguages(langName) {
      if (this.disableCopy) {
        return
      }
      this.disableCopy = true

      Object.keys(this.form.languages).forEach(key => {
        if (key === langName) {
          return
        }
        this.setAvailableLanguage(this.form.languages[key].language, this.form.languages[langName].items.map(item => {
          return {
            name: item.name,
            description: item.description,
            uniqueId: item.uniqueId,
          }
        }))
        this.form.languages[key].error_validate = false
      })
    },
    toggleShowEditLanguagesModal(status = false) {
      this.showEditLanguagesModal = status
    },
    toggleShowFaqModal(status = false, faq = null) {
      this.editableFaq = faq
      this.showFaqModal = status
    },
    toggleShowDeleteModal(status = false) {
      this.showDeleteModal = status
    },
    updateLanguages(newLanguages) {
      const sortedLanguages = newLanguages.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
      const newLangNames = sortedLanguages.map(lang => lang.name)

      // Обновляем языки
      sortedLanguages.forEach(lang => {
        if (!this.form.languages[lang.name]) {
          this.setAvailableLanguage({ ...lang, id: lang.value })
        } else {
          this.setAvailableLanguage({ ...lang, id: lang.value }, this.form.languages[lang.name].items)
        }
      })

      // Удаляем языки, которых нет в newLanguages
      Object.keys(this.form.languages).forEach(existingLangName => {
        if (!newLangNames.includes(existingLangName)) {
          const isActiveLangBeingDeleted = (existingLangName === this.activeLanguageName)

          this.$delete(this.form.languages, existingLangName)

          // Если удалили активный язык — обновляем activeLanguage
          if (isActiveLangBeingDeleted) {
            const remainingLanguages = Object.values(this.form.languages)
            if (remainingLanguages.length > 0) {
              this.activeLanguage = remainingLanguages[0].language.id
            } else {
              this.activeLanguage = null
            }
          }
        }
      })

      // Переупорядочиваем form.languages по отсортированным newLangNames
      const reorderedLanguages = {}
      newLangNames.forEach(langName => {
        if (this.form.languages[langName]) {
          reorderedLanguages[langName] = this.form.languages[langName]
        }
      })

      // Очищаем старый объект, чтобы сохранить реактивность
      Object.keys(this.form.languages).forEach(key => {
        this.$delete(this.form.languages, key)
      })

      // Записываем заново, в нужном порядке
      Object.entries(reorderedLanguages).forEach(([key, value]) => {
        this.$set(this.form.languages, key, value)
      })
    },
    createFaq(faq) {
      this.form.languages[this.activeLanguageName].items.push({ uniqueId: getRandomId(this.currentFaqsIds), ...faq })
      this.form.languages[this.activeLanguageName].error_validate = false
      this.disableCopy = false
    },
    updateFaq(updatedFaq) {
      const oldRuleIdx = this.form.languages[this.activeLanguageName].items.findIndex(el => el.uniqueId === updatedFaq.uniqueId)
      if (oldRuleIdx === -1) {
        return
      }
      this.form.languages[this.activeLanguageName].items.splice(oldRuleIdx, 1, updatedFaq)
      this.disableCopy = false
    },
    deleteFaq(deleteFaq) {
      const oldRuleIdx = this.form.languages[this.activeLanguageName].items.findIndex(el => el.uniqueId === deleteFaq.uniqueId)
      if (oldRuleIdx === -1) {
        return
      }
      this.form.languages[this.activeLanguageName].items.splice(oldRuleIdx, 1)
      this.disableCopy = false
    },
    checkingForTabFilling() {
      let isValidFaqs = true
      const languageKeys = Object.keys(this.form.languages)

      languageKeys.forEach((langKey) => {
        if (!this.form.languages[langKey].items?.length) {
          this.form.languages[langKey].error_validate = true
          isValidFaqs = false
        } else {
          this.form.languages[langKey].error_validate = false
        }
      })
      return isValidFaqs
    },
    actionFaqset() {
      // Нужно чтобы отобразить в каких табах есть ошибки валидации
      const isValidFaqs = this.checkingForTabFilling()

      this.$refs.form.validate(async (valid) => {
        if (!valid || !isValidFaqs) {
          this.$notification.error({
            message: 'Please fill in all required fields',
          })
          return false
        }

        const data = this.getPayloadData()
        if (this.isEditMode) {
          this.updateFaqset(data)
          return
        }
        this.createFaqset(data)
      })
    },
    getPayloadData() {
      const items = Object.values(this.form.languages).flatMap(({ language, items }) =>
        items.map(({ id, name, description }) => {
          if (id) {
            return { language_id: language.id, id, name, description }
          }
          return { language_id: language.id, name, description }
        }))

      return {
        name: this.form.name,
        role_id: this.form.role_id,
        default_language_id: items[0].language_id,
        items,
      }
    },
    async createFaqset(data) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/faq-sets`
        const response = await apiClient.post(url, data)
        if (response?.data?.data) {
          this.$notification.success({
            message: 'FAQsets successfully created',
            description: '',
          })
          this.$router.push('/referral/faqsets')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async updateFaqset(data) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/faq-sets/${this.faqsetId}`
        const response = await apiClient.put(url, data)
        if (response?.data?.data) {
          this.$notification.success({
            message: 'FAQsets successfully updated',
            description: '',
          })
          this.$router.push('/referral/faqsets')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async removeFaqset() {
      try {
        this.loading = true
        const url = `${referralApiUrl}/faq-sets/${this.faqsetId}`
        const response = await apiClient.delete(url)
        if (response?.status >= 200 && response?.status < 300) {
          this.$notification.success({
            message: 'FAQsets successfully removed',
            description: '',
          })
          this.$router.push('/referral/faqsets')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async getFaqSetParams() {
      try {
        const url = `${referralApiUrl}/faq-sets/${this.faqsetId}`

        const response = await apiClient.get(url)
        if (response?.data?.data) {
          const { data } = response.data
          this.currentFaqset = data
          this.form.name = data.name
          this.form.role_id = data.role.id
          data.items.forEach(item => {
            const filledItem = {
              id: item.id,
              name: item.name,
              description: item.description,
              uniqueId: item.id,
            }
            const currentItems = this.form.languages[item.language.name]?.items || []
            this.setAvailableLanguage(
              { ...item.language },
              [...currentItems, filledItem],
            )
          })
          this.activeLanguage = data.items[0]?.language?.id
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
    async getAvailableRoles() {
      try {
        this.getAvailableRolesLoading = true
        this.availableRoles = await getReferralRoles() || []
      } finally {
        this.getAvailableRolesLoading = false
      }
    },
    async getAvailableLanguages() {
      try {
        this.getAvailableLanguagesLoading = true
        const allLanguages = await getReferralLanguages() || []
        if (allLanguages) {
          this.availableLanguages = allLanguages.map(lang => {
            const langObject = {
              id: lang.id,
              name: lang.name,
            }
            if (lang.name === defaultLanguageName && !this.faqsetId) {
              this.updateLanguages([{
                name: lang.name,
                value: lang.id,
                is_default: true,
              }])
            }
            return langObject
          })
        }
      } finally {
        this.getAvailableLanguagesLoading = false
      }
    },
  },
  async mounted() {
    this.faqsetId = this.$route.params.id

    try {
      this.loading = true
      const requests = [this.getAvailableRoles(), this.getAvailableLanguages()]

      if (this.faqsetId) {
        requests.push(this.getFaqSetParams())
      }
      await Promise.all(requests)
    } finally {
      this.loading = false
    }
  },
}
</script>

<style lang="scss">
.faqsets-form {
  &__handle {
    transition: width 2s ease;
    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      align-self: flex-start;
      height: 46px;
      width: 20px;
      min-width: 20px;
      transition: all .3s ease;
      &__hide {
        width: 0;
        min-width: 0;
      }
      &__show {
        width: 20px;
        min-width: 20px;
      }
    }
  }
  &__faqs {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  &__faq {
    display: flex;
    align-items: center;
  }
  &__collapse {
    min-width: 500px;
  }
}
</style>
